import { isCanada } from 'app/common/websiteCountry';

import ClickOutside from 'app/widgets/ClickOutside';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { AdContext } from '../ReactContexts/adContext';
import {
  DealerListContext,
  DealerTabs,
} from '../ReactContexts/dealerListContext';
import { EquipmentListContext } from '../ReactContexts/equipmentListContext';
import { LayoutContext } from '../ReactContexts/layoutContext';
import { VerifyEmailMenu } from '../VerifyEmailMenu';
import SmallAdBanner from './SmallAdBanner';
import useFlag from 'app/hooks/useFlag';
import { FeatureName } from 'types/entities/MobileSettings';
import { LanguageSwitcher } from 'app/components/LanguageSwitcher';
import { useMemo } from 'react';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { useAuth } from 'context';
import clsx from 'clsx';
import { mobileSettingsApi } from 'services/api/mobile-settings/queries';

export function KubotaMenu() {
  const isAccessSharingPageEnabled = useFlag(FeatureName.ACCESS_SHARING);

  const { data: user } = useGetUserInfoQuery();
  const { isAuthenticated } = useAuth();

  const { removeSpecificClass, classNames } = React.useContext(LayoutContext);
  const { equipmentListData } = React.useContext(EquipmentListContext);
  const { adData, setAdData } = React.useContext(AdContext);
  const { data: { ad } = {} } = mobileSettingsApi.useGetMobileSettingsQuery();
  const { setDealerListData } = React.useContext(DealerListContext);

  const handleClick = () => {
    removeSpecificClass('mobile-nav-shown');
  };

  const handleClickOutside = () => {
    if (
      classNames.filter(className => className.class == 'mobile-nav-shown')
        .length > 0
    )
      handleClick();
  };

  const shouldShowSmallAdBanner = () => {
    return (
      !!adData?.smallBannerAd?.buttonLabel &&
      !!adData?.smallBannerAd?.buttonUrl &&
      !!adData?.smallBannerAd?.description &&
      !!adData?.smallBannerAd?.imageUrl &&
      !!adData?.smallBannerAd?.title
    );
  };

  React.useEffect(() => {
    if (!shouldShowSmallAdBanner() && ad) {
      const { locale, ...adData } = ad;
      // TODO eliminate this useless AdContext at a later time
      setAdData(adData as any);
    }
  }, []);

  function handleLogoClick() {
    if (isAuthenticated) {
      navigate('/equipment', {
        state: { scrollToEquipmentId: equipmentListData.selectedEquipment?.id },
      });
    } else {
      navigate('/login');
    }

    handleClick();
  }

  const handleEquipmentClick = () => {
    removeSpecificClass('equipment-details-visible');
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isMenuOpen = useMemo(
    () => classNames.some(className => className.class === 'mobile-nav-shown'),
    [classNames],
  );

  let selectedIndex = -1;

  if (location.pathname.includes('/equipment')) {
    selectedIndex = 0;
  }
  if (
    location.pathname.includes('/resources') ||
    location.pathname.includes('/models') ||
    location.pathname.includes('/faultCodes') ||
    location.pathname.includes('/manual') ||
    location.pathname.includes('/videoResources') ||
    location.pathname.includes('/guides')
  ) {
    selectedIndex = 2;
  }
  if (location.pathname.includes('/dealers')) {
    selectedIndex = 3;
  }
  if (location.pathname.includes('/access')) {
    selectedIndex = 4;
  }
  if (location.pathname.includes('/about')) {
    selectedIndex = 5;
  }

  const EquipmentSvg = (
    <svg
      id="ic_menu_equipment"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <rect id="Box" width="24" height="24" fill="none" />
      <path
        id="Logo"
        d="M21197.7,22166.4v-.432c0-4.619,5.158-8.381,11.5-8.381s11.5,3.75,11.5,8.381v.432c0,4.617-5.154,8.383-11.5,8.383S21197.7,22171.018,21197.7,22166.4Zm.473-2.049a6.964,6.964,0,0,0,1.35,6.043c.168.232.363.545.49.756a11.97,11.97,0,0,0,6.08,3.111,13.966,13.966,0,0,1-1.828-.83,9.9,9.9,0,0,1-5.414-4.236,13.566,13.566,0,0,0,2.545,2.207,14.389,14.389,0,0,0,7.807,2.123,12.992,12.992,0,0,0,8.871-3.07c.744-.705,2.176-2.652,2.186-3.863h0s0-.008,0-.012h0a6.1,6.1,0,0,0-1.588-4.76,9.9,9.9,0,0,0-2.066-1.691h0a14.172,14.172,0,0,0-7.4-1.99C21203.895,22158.139,21199.426,22160.789,21198.172,22164.352Zm22.088,2.236.006.029c0,2.186-1.395,4.172-3.582,5.57a16.978,16.978,0,0,1-1.8,1.285c3.363-1.42,5.623-4.059,5.623-7.072v-.205A2.492,2.492,0,0,1,21220.26,22166.588Zm-22.107-2.326a8.492,8.492,0,0,1,3.6-4.162c0-.008,0-.01.01-.02l1.184-.918A8.7,8.7,0,0,0,21198.152,22164.262Zm.9,1.059c0-3.705,4.545-6.715,10.146-6.715a14.23,14.23,0,0,1,5.406,1.031,9.711,9.711,0,0,1,2.953,1.9l0-.01c.02.023.051.057.086.094.131.127.252.258.363.389.141.16.273.32.395.486a3.318,3.318,0,0,1,.545.9,4.4,4.4,0,0,1,.445,1.9,4.51,4.51,0,0,1-.381,1.795l.008-.008-.049.119a3.609,3.609,0,0,1-.35.633,6.28,6.28,0,0,1-.686.9,8.527,8.527,0,0,1-2.6,1.938,13.731,13.731,0,0,1-6.137,1.367C21203.611,22172.037,21199.055,22169.027,21199.055,22165.32Zm6.629,1.758c-.539,1.637-.973,2.959-1.025,3.107a15.252,15.252,0,0,0,9.049.006c-.666-.523-6.5-5.1-6.58-5.17l-.014-.012-.082-.143c.033-.012.033-.012.033-.025s0-.012.014-.023l.07-.121.012-.012c.105-.08,5.771-3.934,6.486-4.418a14.757,14.757,0,0,0-4.375-.643c-.346,0-.707.012-1.064.037l-.066-.008Zm.975-7.584a.082.082,0,0,1,.025.059v.037l0,0a.011.011,0,0,1,0,.01.033.033,0,0,1-.021.043l-3.373,10.008a.48.48,0,0,1-.105.156.011.011,0,0,1,0,.01.044.044,0,0,1-.037.027.671.671,0,0,1-.113.08l1.584.25v0c-.3-.234.02-.4.053-.641a79.818,79.818,0,0,1,2.006-7.916c.121-.387.379-1.611.5-2.08l-.52-.061A.031.031,0,0,1,21206.658,22159.494Zm12.008,6.668a5.882,5.882,0,0,1-3.055,3.531.039.039,0,0,0,.027.018.16.16,0,0,0,.107.051l.072-.014a.072.072,0,0,1,.035.008,7.081,7.081,0,0,0,2.318-1.939v-.014h.012a2.571,2.571,0,0,0,.719-.584,4.516,4.516,0,0,0,.441-1.922,4.3,4.3,0,0,0-.361-1.709A9.777,9.777,0,0,1,21218.666,22166.162Zm-15.512,3.6a.414.414,0,0,0,.074-.119l3.361-9.959a14.644,14.644,0,0,0-2.414.652c-2.705,1.055-4.449,2.914-4.449,4.941a3.748,3.748,0,0,0,.268,1.379c.008.018.012.031.02.049l.014.021h0A6.323,6.323,0,0,0,21203.154,22169.758Zm12.281-8.682-.014.012c-.049.035-5.357,3.586-5.635,3.768l-.072.107.061.105c.225.191,5.055,4,5.746,4.551a.025.025,0,0,1,.012,0c2-1.105,3.141-2.687,3.141-4.344,0-1.617-1.113-3.186-3.051-4.289A.594.594,0,0,0,21215.436,22161.076Zm-8.225,3.68-.051.086.063.109a.638.638,0,0,1,.061.049l.262.01s1.727,1.283,2.9,2.051a16.33,16.33,0,0,0,3.746,1.617c-1.7-1.34-4.373-3.455-4.465-3.547l-.014-.014-.084-.143a.038.038,0,0,1,0-.049l.035-.053-2.41-.146A.453.453,0,0,0,21207.211,22164.756Zm-7.328-1.828.014.008a3.379,3.379,0,0,0-.148,3.215,3.677,3.677,0,0,1-.105-.877,4.337,4.337,0,0,1,1.34-3.029,6.8,6.8,0,0,1,1.8-1.516,12.477,12.477,0,0,1,3.047-1.211l.721-.158a.015.015,0,0,0,0,.006,15.136,15.136,0,0,1,2.719-.244,13,13,0,0,1,6.008,1.387c.25.125.492.26.725.4.025.014.051.027.08.045a6.3,6.3,0,0,1,1.549,1.344c.1.115.188.23.266.344a1.31,1.31,0,0,0-.234-.895,9.16,9.16,0,0,0-.779-.674,12.906,12.906,0,0,0-7.684-2.34C21204.967,22158.736,21201.338,22160.475,21199.883,22162.928Z"
        transform="translate(-21197.199 -22154.186)"
        fill="#909090"
      />
    </svg>
  );

  const ResourcesSvg = (
    <svg
      id="ic_menu_resources"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <rect id="Box" width="24" height="24" fill="none" />
      <g id="Icon" transform="translate(1.82 1.128)">
        <path
          id="Vector"
          d="M18.249,2.476,14.784,15.949H-.817L3.68,1.406A1.975,1.975,0,0,1,5.556,0H16.338a1.957,1.957,0,0,1,1.815,1.22A1.968,1.968,0,0,1,18.249,2.476Z"
          transform="translate(0.997 3.739)"
          fill="none"
          stroke="#909090"
          strokeWidth="1.2"
        />
        <path
          id="Vector-2"
          d="M0,14.146H4.19a1.952,1.952,0,0,0,1.937-2.1L5.26,0"
          transform="translate(14.048 5.542)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-3"
          d="M0,3.819.912,0"
          transform="translate(8.507 2.059)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-4"
          d="M0,3.837.824,0"
          transform="translate(14.381 2.05)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-5"
          d="M0,0H7.013"
          transform="translate(6.772 10.847)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-6"
          d="M0,0H7.013"
          transform="translate(5.895 14.383)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
      </g>
    </svg>
  );

  const DealersSvg = (
    <svg
      id="ic_menu_dealers"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <rect id="Box" width="24" height="24" fill="none" />
      <g id="Icon">
        <path
          id="Vector"
          d="M15.368.162,5.751,2.573A4.786,4.786,0,0,0,2.574,5.75L.162,15.367c-.726,2.906,1.056,4.7,3.971,3.971l9.617-2.4a4.825,4.825,0,0,0,3.177-3.177l2.412-9.627C20.065,1.227,18.274-.565,15.368.162Z"
          transform="translate(2.25 2.322)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-2"
          d="M6,3A3,3,0,1,1,3,0,3,3,0,0,1,6,3Z"
          transform="translate(9 9.072)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
      </g>
    </svg>
  );

  const AccessSharingSvg =
    selectedIndex != 4 ? (
      <svg
        id="ic_menu_access_sharing"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
      >
        <rect id="BG" width="24" height="24" fill="none" />
        <g id="icon" transform="translate(2 2.994)">
          <g id="Person" transform="translate(5.967 2.636)">
            <g
              id="Rectangle_315"
              data-name="Rectangle 315"
              transform="translate(0 8.358)"
              fill="none"
              stroke="#909090"
              strokeWidth="1.2"
            >
              <path
                d="M3,0h8.033a3,3,0,0,1,3,3V7.016a0,0,0,0,1,0,0H0a0,0,0,0,1,0,0V3A3,3,0,0,1,3,0Z"
                stroke="none"
              />
              <path
                d="M3,.6h8.033a2.4,2.4,0,0,1,2.4,2.4V5.816a.6.6,0,0,1-.6.6H1.2a.6.6,0,0,1-.6-.6V3A2.4,2.4,0,0,1,3,.6Z"
                fill="none"
              />
            </g>
            <path
              id="Path_1103"
              data-name="Path 1103"
              d="M22522-5571.061h0a.217.217,0,0,0-.068-.006h-.145a3.8,3.8,0,0,1-2.6-1.106,3.613,3.613,0,0,1-1.037-2.546,3.666,3.666,0,0,1,3.664-3.659,3.663,3.663,0,0,1,3.658,3.659,3.667,3.667,0,0,1-3.477,3.658Zm-.168-1.32h0a.388.388,0,0,1,.043,0h.008l.035,0,.02.006.021-.006a2.367,2.367,0,0,0,2.223-2.351,2.362,2.362,0,0,0-2.363-2.357,2.358,2.358,0,0,0-2.355,2.357,2.347,2.347,0,0,0,2.262,2.351l.1.006.006-.012Z"
              transform="translate(-22514.797 5578.378)"
              fill="#909090"
            />
          </g>
          <g id="Person-2" data-name="Person">
            <path
              id="Path_1105"
              data-name="Path 1105"
              d="M23264.779-5381.905h-.184a2.69,2.69,0,0,1-2.6-2.675,2.692,2.692,0,0,1,2.682-2.691,2.687,2.687,0,0,1,2.688,2.681,2.679,2.679,0,0,1-2.582,2.686Zm-.072-.992c.037,0,.057.006.072.006h.025l.016,0h.021a1.686,1.686,0,0,0,1.555-1.7,1.694,1.694,0,0,0-1.7-1.688,1.689,1.689,0,0,0-1.687,1.688,1.7,1.7,0,0,0,1.6,1.7h.049v-.011Z"
              transform="translate(-23259.313 5387.271)"
              fill="#909090"
            />
            <path
              id="Path_1104"
              data-name="Path 1104"
              d="M23154.357-5308.648h4.422s.783.042.77-.6-.77-.552-.77-.552h-3.273v-1.082a1.715,1.715,0,0,1,1.715-1.714h3.736a.571.571,0,0,0,.459-.593.512.512,0,0,0-.459-.555h-3.736a2.866,2.866,0,0,0-2.863,2.862v2.229Z"
              transform="translate(-23154.357 5320.157)"
              fill="#909090"
            />
          </g>
        </g>
      </svg>
    ) : (
      <svg
        id="ic_menu_access_sharing_selected"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
      >
        <rect id="BG" width="24" height="24" fill="none" />
        <g id="icon" transform="translate(-616.646 -257.207)">
          <g id="Person" transform="translate(624.614 262.837)">
            <g
              id="Rectangle_315"
              data-name="Rectangle 315"
              transform="translate(0 8.358)"
              fill="#dc4405"
              stroke="#dc4405"
              strokeWidth="1.2"
            >
              <path
                d="M3,0h8.033a3,3,0,0,1,3,3V7.016a0,0,0,0,1,0,0H0a0,0,0,0,1,0,0V3A3,3,0,0,1,3,0Z"
                stroke="none"
              />
              <path
                d="M3,.6h8.033a2.4,2.4,0,0,1,2.4,2.4V5.816a.6.6,0,0,1-.6.6H1.2a.6.6,0,0,1-.6-.6V3A2.4,2.4,0,0,1,3,.6Z"
                fill="none"
              />
            </g>
            <path
              id="Path_1103"
              data-name="Path 1103"
              d="M22522-5571.061h0a.217.217,0,0,0-.068-.006h-.145a3.8,3.8,0,0,1-2.6-1.106,3.613,3.613,0,0,1-1.037-2.546,3.666,3.666,0,0,1,3.664-3.659,3.663,3.663,0,0,1,3.658,3.659,3.667,3.667,0,0,1-3.477,3.658Z"
              transform="translate(-22514.797 5578.378)"
              fill="#dc4405"
            />
          </g>
          <g
            id="Person-2"
            data-name="Person"
            transform="translate(618.646 260.201)"
          >
            <path
              id="Path_1105"
              data-name="Path 1105"
              d="M23264.779-5381.905h-.184a2.69,2.69,0,0,1-2.6-2.675,2.692,2.692,0,0,1,2.682-2.691,2.687,2.687,0,0,1,2.688,2.681,2.679,2.679,0,0,1-2.582,2.686Zm-.072-.992c.037,0,.057.006.072.006h.025l.016,0h.021a1.686,1.686,0,0,0,1.555-1.7,1.694,1.694,0,0,0-1.7-1.688,1.689,1.689,0,0,0-1.687,1.688,1.7,1.7,0,0,0,1.6,1.7h.049v-.011Z"
              transform="translate(-23259.313 5387.271)"
              fill="#dc4405"
            />
            <path
              id="Path_1104"
              data-name="Path 1104"
              d="M23154.357-5308.648h4.422s.783.042.77-.6-.77-.552-.77-.552h-3.273v-1.082a1.715,1.715,0,0,1,1.715-1.714h3.736a.571.571,0,0,0,.459-.593.512.512,0,0,0-.459-.555h-3.736a2.866,2.866,0,0,0-2.863,2.862v2.229Z"
              transform="translate(-23154.357 5320.157)"
              fill="#dc4405"
            />
          </g>
        </g>
      </svg>
    );

  const AboutSvg = (
    <svg
      id="ic_menu_about"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <rect id="Box" width="24" height="24" fill="none" />
      <g id="icon">
        <path
          id="Vector"
          d="M9,18A9,9,0,1,0,0,9,9.026,9.026,0,0,0,9,18Z"
          transform="translate(21 21) rotate(180)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-2"
          d="M0,0V4.5"
          transform="translate(12 15.6) rotate(180)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-3"
          d="M0,0H.008"
          transform="translate(12.006 8.4) rotate(180)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </g>
    </svg>
  );

  const NavClose = (
    <svg
      id="ic_nav_close"
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
    >
      <rect id="Box" width="32" height="32" fill="none" />
      <g id="Icon" transform="translate(-2076.936 -711.936)">
        <line
          id="Line_1"
          data-name="Line 1"
          x2="12"
          y2="12"
          transform="translate(2086.935 721.935)"
          fill="none"
          stroke="#1a1a1a"
          strokeLinecap="round"
          strokeWidth="2"
        />
        <line
          id="Line_2"
          data-name="Line 2"
          x1="12"
          y2="12"
          transform="translate(2086.935 721.935)"
          fill="none"
          stroke="#1a1a1a"
          strokeLinecap="round"
          strokeWidth="2"
        />
      </g>
    </svg>
  );
  return (
    <>
      <ClickOutside
        onClickOutside={handleClickOutside}
        className="layout-clickoutside-submenu"
      >
        <div className="menu-header">
          <span
            className={clsx('site-logo', { 'website-CA': isCanada })}
            onClick={handleLogoClick}
          >
            <img src="/logo.svg" alt="logo" />
          </span>
          <span className="mobile-menu-close" onClick={handleClick}>
            {NavClose}
          </span>
        </div>

        {!isAuthenticated && (
          <LanguageSwitcher
            className="kubota-menu__language-switcher"
            hidden={!isMenuOpen}
          />
        )}

        <div className="nav-side">
          <div className="menu-left">
            <ul className="k-reset k-header k-menu k-menu-vertical">
              {isAuthenticated && (
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 0 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link
                    to="/equipment"
                    state={{
                      scrollToEquipmentId:
                        equipmentListData.selectedEquipment?.id,
                    }}
                    onClick={() => {
                      handleClick();
                      location.pathname.includes('/equipment') &&
                        handleEquipmentClick();
                    }}
                  >
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon menu-icon-equip">
                        {EquipmentSvg}
                      </span>
                      <span className="k-menu-link-text">
                        {t('title_equipment')}
                      </span>
                    </span>
                  </Link>
                </li>
              )}

              <li
                className={`k-item k-menu-item ${
                  selectedIndex === 2 ? 'selected-menu-item' : ''
                }`}
              >
                <Link to="/resources" onClick={handleClick}>
                  <span className="k-link k-menu-link" role="presentation">
                    <span className="menu-icon">{ResourcesSvg}</span>
                    <span className="k-menu-link-text">
                      {t('title_resources')}
                    </span>
                  </span>
                </Link>
              </li>

              {isAuthenticated && (
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 3 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link
                    to="/dealers"
                    onClick={() => {
                      setDealerListData(data => ({
                        ...data,
                        dealerTab: DealerTabs.Locations,
                      }));
                      handleClick();
                    }}
                  >
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon">{DealersSvg}</span>
                      <span className="k-menu-link-text">
                        {t('title_dealers')}
                      </span>
                    </span>
                  </Link>
                </li>
              )}

              {isAuthenticated && isAccessSharingPageEnabled && (
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 4 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link to="/access/equipment-and-fleets" onClick={handleClick}>
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon menu-icon-acess-sharing">
                        {AccessSharingSvg}
                      </span>
                      <span className="k-menu-link-text">
                        {t('title_access_sharing')}
                      </span>
                    </span>
                  </Link>
                </li>
              )}
            </ul>
          </div>
          <div className="menu-left">
            {user && !user?.email_verified && <VerifyEmailMenu />}

            {(user?.email_verified || !user) && shouldShowSmallAdBanner() && (
              <SmallAdBanner bannerData={adData?.smallBannerAd} />
            )}
          </div>

          {isAuthenticated && (
            <div className="menu-left">
              <ul className="k-reset k-header k-menu k-menu-vertical">
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 5 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link to="/about" onClick={handleClick}>
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon">{AboutSvg}</span>
                      <span className="k-menu-link-text">
                        {t('about_preference_title')}
                      </span>
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
      </ClickOutside>
    </>
  );
}
